import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Table,
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    CustomInput,
    Label,
    Row,
    Col,
    Input,
    Form,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    CardFooter,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Progress,
    Toast,
    ToastBody
} from 'reactstrap';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import { useParams, useHistory } from 'react-router-dom';

import toolsService from 'services/tools.service';
import kiotvietService from 'services/kiotviet.service';
import downloadService from 'services/toolUpload.service';
import DownloadFileService from 'services/fileManager.service';
import { usePageContext } from 'contexts/PageContext';

// Lấy URL API từ biến môi trường hoặc giá trị mặc định
const API_URL = process.env.REACT_APP_API_URL || "https://api.dhbtools.com/admin";

// Utility function to format file size
const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

function ToolDetails() {
    const { matool } = useParams();
    const history = useHistory();
    const { setBreadcrumb, clearBreadcrumb } = usePageContext();
    const [loading, setLoading] = useState(false);
    const [tool, setTool] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [description, setDescription] = useState('');
    const [descriptionLoading, setDescriptionLoading] = useState(false);
    const maxDescriptionLength = 500; // Giới hạn ký tự cho mô tả

    // Log khi component mount và nhận params
    console.log('ToolDetails mounted with matool:', matool);

    // Quản lý thuộc tính
    const [attributes, setAttributes] = useState([]);

    // Quản lý biến thể
    const [variations, setVariations] = useState([]);

    // Modal thêm/sửa thuộc tính
    const [attributeModal, setAttributeModal] = useState(false);
    const [attributeName, setAttributeName] = useState('');
    const [attributeValues, setAttributeValues] = useState('');
    const [editAttributeIndex, setEditAttributeIndex] = useState(-1);

    // KiotViet Integration State
    const [kiotvietEnabled, setKiotvietEnabled] = useState(false);
    const [kiotvietProducts, setKiotvietProducts] = useState([]);
    const [kiotvietLoading, setKiotvietLoading] = useState(false);
    const [kiotvietSaveLoading, setKiotvietSaveLoading] = useState(false);
    const [defaultProductCode, setDefaultProductCode] = useState('');
    const [defaultProductId, setDefaultProductId] = useState(null);
    const [variationMappings, setVariationMappings] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState({});

    // Password editing state
    const [editingPassword, setEditingPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    // Tool file upload state
    const [toolUploadProgress, setToolUploadProgress] = useState(null);
    const [toolUploading, setToolUploading] = useState(false);
    const toolFileInputRef = useRef(null);
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Tool ID alias mapping (old name -> new name and vice versa)
    const toolAliasMap = {
        'dhbreupfacebook': 'dhbuploadfacebook',
        'dhbreupyoutube': 'dhbuploadyoutube',
        'dhbreupinstagram': 'dhbuploadinstagram',
        'dhbreuptiktok': 'dhbuploadtiktok',
        'dhbreuptwitter': 'dhbuploadtwitter',
        // Reverse mappings
        'dhbuploadfacebook': 'dhbreupfacebook',
        'dhbuploadyoutube': 'dhbreupyoutube',
        'dhbuploadinstagram': 'dhbreupinstagram',
        'dhbuploadtiktok': 'dhbreuptiktok',
        'dhbuploadtwitter': 'dhbreuptwitter',
    };

    // Filter KiotViet products dựa trên mã công cụ (lấy prefix trước _)
    const filteredKiotvietProducts = useMemo(() => {
        if (!tool?.matool || !kiotvietProducts.length) return kiotvietProducts;
        // Lấy prefix trước underscore: vd dhbreupfacebook_v5 -> dhbreupfacebook
        const toolPrefix = tool.matool.split('_')[0].toLowerCase();
        // Tìm alias nếu có
        const aliasPrefix = toolAliasMap[toolPrefix];

        return kiotvietProducts.filter(product => {
            const code = product.code?.toLowerCase();
            // Match với cả toolPrefix và aliasPrefix
            return code?.startsWith(toolPrefix) || (aliasPrefix && code?.startsWith(aliasPrefix));
        });
    }, [tool?.matool, kiotvietProducts]);

    // Định dạng số tiền VND
    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Chuyển đổi từ định dạng VND sang số nguyên để lưu trữ
    const parseCurrency = (formattedAmount) => {
        if (!formattedAmount) return '0';
        return formattedAmount.toString().replace(/\./g, '');
    };

    // Tính số ngày từ thời hạn
    const calculateDaysFromDuration = (duration) => {
        if (!duration) return 0;

        // Lấy giá trị số từ thời hạn
        const numericValue = parseInt(duration.match(/\d+/)?.[0] || 0);

        if (duration.includes('tháng')) {
            return numericValue * 30;
        } else if (duration.includes('năm')) {
            return numericValue * 365;
        } else if (duration.toLowerCase().includes('vĩnh viễn')) {
            return 10 * 365; // Vĩnh viễn = 10 năm
        }

        return 0;
    };

    useEffect(() => {
        if (matool) {
            console.log('Loading tool details for matool:', matool);
            loadToolDetails();
            loadThumbnail();
            loadKiotVietProducts();
            loadKiotVietMapping();
        }

        // Cleanup: clear breadcrumb when component unmounts
        return () => {
            clearBreadcrumb();
        };
    }, [matool]);

    // Tải danh sách sản phẩm KiotViet
    const loadKiotVietProducts = async () => {
        try {
            const response = await kiotvietService.getProducts();
            if (response && response.success) {
                setKiotvietProducts(response.data || []);
            }
        } catch (error) {
            console.error('Lỗi khi tải danh sách sản phẩm KiotViet:', error);
        }
    };

    // Tải KiotViet mapping
    const loadKiotVietMapping = async () => {
        try {
            setKiotvietLoading(true);
            const response = await toolsService.getKiotVietMapping(matool);
            if (response && response.success) {
                const mapping = response.kiotvietMapping || {};
                setKiotvietEnabled(mapping.enabled || false);
                setDefaultProductCode(mapping.defaultProductCode || '');
                setDefaultProductId(mapping.defaultProductId || null);
                setVariationMappings(mapping.variationMappings || []);
            }
        } catch (error) {
            console.error('Lỗi khi tải KiotViet mapping:', error);
        } finally {
            setKiotvietLoading(false);
        }
    };

    // Lưu KiotViet mapping
    const saveKiotVietMapping = async () => {
        try {
            setKiotvietSaveLoading(true);
            const mapping = {
                enabled: kiotvietEnabled,
                defaultProductCode,
                defaultProductId,
                variationMappings
            };

            const response = await toolsService.updateKiotVietMapping(matool, mapping);
            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã lưu KiotViet mapping',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
            } else {
                throw new Error(response?.message || 'Lỗi không xác định');
            }
        } catch (error) {
            console.error('Lỗi khi lưu KiotViet mapping:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể lưu KiotViet mapping: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setKiotvietSaveLoading(false);
        }
    };

    // Handler cho upload file phần mềm
    const handleToolFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setToolUploading(true);
        setToolUploadProgress(0);

        downloadService.uploadFileChunked(matool, file, (progressEvent) => {
            const progress = progressEvent.progress || Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setToolUploadProgress(progress);
        })
            .then(() => {
                Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã tải lên tệp thành công!' });
                // Reload tool info
                loadToolDetails();
            })
            .catch((error) => {
                console.error('Upload error:', error);
                Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể tải lên tệp: ' + (error.message || 'Lỗi không xác định') });
            })
            .finally(() => {
                setToolUploading(false);
                setToolUploadProgress(null);
                // Reset input để có thể upload lại cùng file
                if (toolFileInputRef.current) {
                    toolFileInputRef.current.value = '';
                }
            });
    };

    // Handler cho toggle xác thực tải về
    // API: PUT /tools/:id/auth với body { downloadRequireAuth: bool }
    const handleAuthToggle = async () => {
        const newValue = !tool.downloadRequireAuth;

        try {
            await downloadService.updateRequireAuthen(matool, { downloadRequireAuth: newValue });
            // Update local state
            setTool({ ...tool, downloadRequireAuth: newValue });
            Swal.fire({
                icon: 'success',
                title: 'Thành công',
                text: `Đã ${newValue ? 'bật' : 'tắt'} xác thực tải về`,
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Auth toggle error:', error);
            Swal.fire({ icon: 'error', title: 'Lỗi', text: 'Không thể cập nhật: ' + (error.message || 'Lỗi không xác định') });
        }
    };

    // Cập nhật mapping cho một biến thể
    const updateVariationMapping = (expired, version, productCode, productId) => {
        const newMappings = [...variationMappings];
        const existingIndex = newMappings.findIndex(
            m => m.expired === expired && m.version === version
        );

        if (productCode) {
            if (existingIndex >= 0) {
                newMappings[existingIndex] = { expired, version, productCode, productId };
            } else {
                newMappings.push({ expired, version, productCode, productId });
            }
        } else {
            // Xóa mapping nếu không có productCode
            if (existingIndex >= 0) {
                newMappings.splice(existingIndex, 1);
            }
        }

        setVariationMappings(newMappings);
    };

    // Lấy productCode cho một biến thể
    const getVariationProductCode = (expired, version) => {
        const mapping = variationMappings.find(
            m => m.expired === expired && (m.version === version || (!m.version && !version))
        );
        return mapping?.productCode || '';
    };

    // Tải thumbnail từ API
    const loadThumbnail = async () => {
        try {
            console.log('Bắt đầu tải thumbnail cho matool:', matool);

            // Tạo URL trực tiếp để hiển thị ảnh
            // Sử dụng timestamp để tránh cache
            const timestamp = new Date().getTime();
            const imageUrl = `${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`;

            console.log('URL ảnh thumbnail:', imageUrl);

            // Cập nhật state với URL ảnh
            setThumbnailPreview(imageUrl);
        } catch (error) {
            console.error('Lỗi khi tải thumbnail:', error);
            // Trong trường hợp lỗi, vẫn cố gắng hiển thị ảnh từ URL trực tiếp với timestamp mới
            const timestamp = new Date().getTime();
            setThumbnailPreview(`${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`);
        }
    };

    // Tải thông tin chi tiết công cụ và biến thể
    const loadToolDetails = async () => {
        try {
            console.log('Start loading tool details');
            setLoading(true);

            // Tải thông tin công cụ
            const response = await toolsService.getToolById(matool);
            console.log('API response:', response);

            if (!response || !response.success || !response.tool) {
                Swal.fire({
                    title: 'Lỗi!',
                    text: 'Không tìm thấy thông tin công cụ',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                history.push('/admin/tools');
                return;
            }

            const toolData = response.tool;
            setTool(toolData);

            // Cập nhật giá trị description
            setDescription(toolData.description || '');

            // Set breadcrumb
            setBreadcrumb(
                [
                    { label: 'Tool Management', to: '/admin/tools' },
                    { label: toolData.name }
                ],
                '/admin/tools'
            );

            // Xử lý biến thể
            if (toolData && toolData.variations) {
                processToolVariations(toolData);
            } else {
                console.log('Không có dữ liệu biến thể');
                setAttributes([]);
                setVariations([]);
            }
        } catch (error) {
            console.error('Lỗi khi tải dữ liệu công cụ:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể tải dữ liệu công cụ: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
            history.push('/admin/tools');
        } finally {
            setLoading(false);
        }
    };

    // Xử lý dữ liệu biến thể
    const processToolVariations = (toolData) => {
        console.log('Xử lý biến thể:', toolData.variations);
        // Lấy dữ liệu variations từ tool được chọn
        const toolVariations = toolData.variations || [];

        if (toolVariations.length === 0) {
            // Nếu không có dữ liệu variations, đặt mảng rỗng và không hiển thị gì
            setAttributes([]);
            setVariations([]);
            return;
        }

        // Trích xuất các attributes từ variations
        const extractedAttributes = {};

        // Lặp qua tất cả variations để thu thập các thuộc tính và giá trị duy nhất
        toolVariations.forEach(variation => {
            if (variation.attributes) {
                Object.entries(variation.attributes).forEach(([attrName, attrValue]) => {
                    if (!extractedAttributes[attrName]) {
                        extractedAttributes[attrName] = new Set();
                    }
                    extractedAttributes[attrName].add(attrValue);
                });
            }
        });

        // Chuyển đổi từ object sang mảng attributes
        const attributesArray = Object.entries(extractedAttributes).map(([name, valuesSet]) => ({
            name,
            values: Array.from(valuesSet)
        }));

        console.log('Attributes trích xuất:', attributesArray);
        setAttributes(attributesArray);

        // Sử dụng variations từ API trực tiếp
        setVariations(toolVariations);
        console.log('Đã đặt variations:', toolVariations);
    };

    // Hiển thị modal thêm/sửa thuộc tính
    const toggleAttributeModal = (index = -1) => {
        setAttributeModal(!attributeModal);

        if (index >= 0 && attributes[index]) {
            // Chế độ sửa
            const attr = attributes[index];
            setAttributeName(attr.name);
            setAttributeValues(attr.values.join(', '));
            setEditAttributeIndex(index);
        } else {
            // Chế độ thêm mới
            setAttributeName('');
            setAttributeValues('');
            setEditAttributeIndex(-1);
        }
    };

    // Thêm/sửa thuộc tính
    const saveAttribute = () => {
        if (!attributeName || !attributeValues) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng nhập tên thuộc tính và các giá trị',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        // Chuyển đổi chuỗi giá trị thành mảng
        const values = attributeValues.split(',').map(value => value.trim()).filter(value => value !== '');

        if (values.length === 0) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng nhập ít nhất một giá trị cho thuộc tính',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        const newAttribute = {
            name: attributeName,
            values: values
        };

        let newAttributes = [...attributes];

        if (editAttributeIndex >= 0) {
            // Chế độ sửa
            newAttributes[editAttributeIndex] = newAttribute;
        } else {
            // Chế độ thêm mới
            newAttributes.push(newAttribute);
        }

        setAttributes(newAttributes);
        setAttributeModal(false);

        // Cập nhật lại biến thể với thuộc tính mới
        generateVariationsFromAttributes(newAttributes);
    };

    // Tạo tất cả các biến thể có thể từ các thuộc tính
    const generateVariationsFromAttributes = (attrs) => {
        if (!attrs || attrs.length === 0) {
            setVariations([]);
            return;
        }

        console.log('Tạo biến thể từ thuộc tính:', attrs);

        // Lưu trữ các biến thể hiện tại để giữ lại giá trị giá và trạng thái
        const currentVariations = [...variations];

        // Hàm đệ quy để tạo tất cả các kết hợp có thể
        const generateCombinations = (attrs, index = 0, current = {}) => {
            if (index === attrs.length) {
                // Đạt đến cuối cùng của thuộc tính, thêm kết hợp hiện tại vào kết quả
                return [current];
            }

            const attribute = attrs[index];
            let combinations = [];

            for (const value of attribute.values) {
                const newCurrent = { ...current };
                newCurrent[attribute.name] = value;
                combinations = [...combinations, ...generateCombinations(attrs, index + 1, newCurrent)];
            }

            return combinations;
        };

        // Tạo tất cả các kết hợp
        const combinations = generateCombinations(attrs);
        console.log('Tất cả các kết hợp có thể:', combinations);

        // Tạo biến thể với kết hợp và giá (mặc định hoặc từ dữ liệu hiện có)
        const newVariations = combinations.map(combination => {
            // Tạo key từ kết hợp để so sánh với dữ liệu hiện có
            const key = Object.entries(combination)
                .map(([attr, value]) => `${attr}:${value}`)
                .sort()
                .join('|');

            // Tìm trong dữ liệu hiện có
            const existingVariation = currentVariations.find(v => {
                if (!v.attributes) return false;

                const existingKey = Object.entries(v.attributes)
                    .map(([attr, value]) => `${attr}:${value}`)
                    .sort()
                    .join('|');
                return existingKey === key;
            });

            // Tìm thuộc tính thời hạn
            const durationValue = Object.entries(combination).find(
                ([key, value]) => key.toLowerCase().includes('thời hạn') || key.toLowerCase().includes('hạn')
            )?.[1];

            // Tự động tính số ngày dựa trên thời hạn nếu không có sẵn
            let days = existingVariation ? existingVariation.days : 0;
            if (!days && durationValue) {
                days = calculateDaysFromDuration(durationValue);
            }

            // Tạo biến thể mới hoặc giữ lại giá trị hiện có
            return {
                attributes: combination,
                price: existingVariation ? existingVariation.price : '0',
                days: days,
                enabled: existingVariation ? existingVariation.enabled : true
            };
        });

        console.log('Biến thể mới tạo:', newVariations);
        setVariations(newVariations);
    };

    // Xóa thuộc tính
    const deleteAttribute = (index) => {
        Swal.fire({
            title: 'Xác nhận xóa?',
            text: 'Bạn có chắc chắn muốn xóa thuộc tính này? Tất cả biến thể liên quan sẽ bị xóa.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then((result) => {
            if (result.isConfirmed) {
                const newAttributes = [...attributes];
                newAttributes.splice(index, 1);
                setAttributes(newAttributes);

                // Tạo lại các biến thể với thuộc tính mới
                generateVariationsFromAttributes(newAttributes);
            }
        });
    };

    // Bật/tắt biến thể
    const toggleVariationStatus = (index) => {
        const updatedVariations = [...variations];
        updatedVariations[index].enabled = !updatedVariations[index].enabled;
        setVariations(updatedVariations);
    };

    // Xóa biến thể
    const deleteVariation = (index) => {
        Swal.fire({
            title: 'Xác nhận xóa?',
            text: 'Bạn có chắc chắn muốn xóa biến thể này?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy'
        }).then((result) => {
            if (result.isConfirmed) {
                // Lấy thông tin biến thể để hiển thị
                const variationToDelete = variations[index];
                let variationInfo = '';

                if (variationToDelete && variationToDelete.attributes) {
                    variationInfo = Object.entries(variationToDelete.attributes)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                }

                // Xóa biến thể khỏi mảng
                const updatedVariations = [...variations];
                updatedVariations.splice(index, 1);
                setVariations(updatedVariations);

                // Thông báo đã xóa thành công
                Swal.fire(
                    'Đã xóa!',
                    variationInfo ? `Đã xóa biến thể: ${variationInfo}` : 'Đã xóa biến thể thành công',
                    'success'
                );
            }
        });
    };

    // Lưu tất cả dữ liệu lên máy chủ
    const saveAll = async () => {
        if (!tool) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không tìm thấy thông tin công cụ',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        try {
            setLoading(true);
            // Chuẩn bị dữ liệu để lưu
            // Chỉ cần gửi mảng variations, API sẽ tự xử lý

            // Đảm bảo tất cả các variations có đủ thông tin cần thiết
            const validatedVariations = variations.map(v => ({
                attributes: v.attributes,
                days: v.days || 0,
                price: v.price || '0',
                enabled: v.enabled !== undefined ? v.enabled : true
            }));

            console.log('Dữ liệu gửi lên:', validatedVariations);

            // Gọi API để cập nhật
            const response = await toolsService.updateVariations(tool.matool, {
                variations: validatedVariations
            });

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã lưu dữ liệu thành công',
                    icon: 'success',
                    confirmButtonText: 'Đồng ý'
                });

                // Tải lại dữ liệu sau khi lưu thành công
                loadToolDetails();
            } else {
                throw new Error('Lỗi khi lưu dữ liệu');
            }
        } catch (error) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không thể lưu dữ liệu: ' + (error.message || 'Lỗi không xác định'),
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
        } finally {
            setLoading(false);
        }
    };

    // Xử lý thay đổi file thumbnail
    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Kiểm tra file có phải là ảnh không
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Vui lòng chọn file ảnh',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Hiển thị preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setThumbnailPreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Lưu file để upload
        setThumbnailFile(file);
    };

    // Upload thumbnail
    const uploadThumbnail = async () => {
        if (!thumbnailFile) {
            Swal.fire({
                title: 'Thông báo',
                text: 'Vui lòng chọn ảnh trước khi cập nhật',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        try {
            setUploadLoading(true);
            console.log('Bắt đầu tải lên thumbnail cho matool:', matool);

            // Tạo FormData để gửi file
            const formData = new FormData();
            formData.append('thumbnail', thumbnailFile);
            console.log('File đã chọn:', thumbnailFile.name, thumbnailFile.type, thumbnailFile.size);

            // Gọi API upload thumbnail
            const response = await toolsService.updateThumbnail(matool, formData);
            console.log('Phản hồi API upload:', response);

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã cập nhật ảnh thumbnail thành công',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                // Tạo URL mới với timestamp để tránh cache
                const timestamp = new Date().getTime();
                const newThumbnailUrl = `${API_URL}/tools/${matool}/thumbnail?t=${timestamp}`;
                console.log('URL ảnh mới sau khi cập nhật:', newThumbnailUrl);

                // Cập nhật state với URL mới
                setThumbnailPreview(newThumbnailUrl);
                setThumbnailFile(null);
            } else {
                throw new Error(response?.message || 'Lỗi không xác định');
            }
        } catch (error) {
            console.error('Lỗi khi upload thumbnail:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể cập nhật thumbnail: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setUploadLoading(false);
        }
    };

    // Lưu mô tả
    const saveDescription = async () => {
        if (!tool) {
            Swal.fire({
                title: 'Lỗi!',
                text: 'Không tìm thấy thông tin công cụ',
                icon: 'error',
                confirmButtonText: 'Đồng ý'
            });
            return;
        }

        try {
            setDescriptionLoading(true);
            console.log('Bắt đầu cập nhật mô tả cho matool:', matool);

            // Gọi API để cập nhật mô tả
            const response = await toolsService.updateDescription(matool, description);
            console.log('Phản hồi API cập nhật mô tả:', response);

            if (response && response.success) {
                Swal.fire({
                    title: 'Thành công!',
                    text: 'Đã cập nhật mô tả thành công',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                // Cập nhật tool state để hiển thị mô tả mới
                setTool({
                    ...tool,
                    description: description
                });
            } else {
                throw new Error(response?.message || 'Lỗi không xác định');
            }
        } catch (error) {
            console.error('Lỗi khi cập nhật mô tả:', error);
            Swal.fire({
                title: 'Lỗi!',
                text: `Không thể cập nhật mô tả: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setDescriptionLoading(false);
        }
    };

    // Quay lại trang danh sách công cụ
    const goBack = () => {
        history.push('/admin/tools');
    };

    if (loading) {
        return (
            <div className="content">
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Đang tải...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!tool) {
        return (
            <div className="content">
                <Card>
                    <CardBody>
                        <div className="text-center p-5">
                            <h3>Không tìm thấy thông tin công cụ</h3>
                            <Button color="primary" onClick={goBack}>
                                Quay lại danh sách
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        );
    }

    return (
        <div className="content">
            {/* Custom CSS for switches, price inputs, dropdowns, modals, swal */}
            <style>{`
                /* Switches - 24px height with white circle indicator */
                .custom-switch-lg {
                    padding-left: 0 !important;
                    margin-left: 0 !important;
                }
                .custom-switch-lg .custom-control-label {
                    padding-left: 56px !important;
                    line-height: 24px !important;
                    margin-left: 0 !important;
                }
                .custom-switch-lg .custom-control-label::before {
                    width: 44px !important;
                    height: 24px !important;
                    border-radius: 12px !important;
                    background-color: #adb5bd !important;
                    border-color: #adb5bd !important;
                    left: 0 !important;
                    top: 0 !important;
                }
                .custom-switch-lg .custom-control-label::after {
                    width: 20px !important;
                    height: 20px !important;
                    border-radius: 50% !important;
                    background-color: #fff !important;
                    top: 2px !important;
                    left: 2px !important;
                    transition: transform 0.15s ease-in-out !important;
                }
                .custom-switch-lg .custom-control-input:checked ~ .custom-control-label::before {
                    background-color: #28a745 !important;
                    border-color: #28a745 !important;
                }
                .custom-switch-lg .custom-control-input:checked ~ .custom-control-label::after {
                    transform: translateX(20px) !important;
                    background-color: #fff !important;
                }
                
                /* Price Input Group - Pill shape border-radius */
                .price-input-group {
                    width: 160px !important;
                    vertical-align: middle !important;
                    margin: 0 !important;
                }
                .price-input-group .form-control {
                    border-top-left-radius: 50px !important;
                    border-bottom-left-radius: 50px !important;
                    border-top-right-radius: 0 !important;
                    border-bottom-right-radius: 0 !important;
                    border-right: none !important;
                    height: 31px !important;
                }
                .price-input-group .input-group-text {
                    border-top-right-radius: 50px !important;
                    border-bottom-right-radius: 50px !important;
                    border-top-left-radius: 0 !important;
                    border-bottom-left-radius: 0 !important;
                    border-left: 1px solid #ced4da !important;
                    background-color: #e9ecef !important;
                    height: 31px !important;
                }
                
                /* Dropdown - Better hover and positioning */
                .kiotviet-dropdown .dropdown-menu {
                    right: 0 !important;
                    left: auto !important;
                    min-width: 100%;
                }
                .kiotviet-dropdown .dropdown-item:hover {
                    background-color: #e3f2fd !important;
                    color: #1976d2 !important;
                }
                .kiotviet-dropdown .dropdown-item.active {
                    background-color: #1976d2 !important;
                    color: #fff !important;
                }
                
                /* Modal styling - Centered, no border, shadow like Swal */
                .modal-content {
                    border-radius: 20px !important;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
                    border: none !important;
                    overflow: hidden;
                }
                .modal-header {
                    border-bottom: none !important;
                    padding: 1.25rem 1.5rem;
                }
                .modal-body {
                    padding: 1.5rem 2rem !important;
                }
                .modal-footer {
                    border-top: none !important;
                    padding: 1rem 1.5rem;
                }
                
                /* Modal animation - Pop up like Swal */
                .modal.fade .modal-dialog {
                    transform: scale(0.8);
                    opacity: 0;
                    transition: all 0.2s ease-out;
                }
                .modal.show .modal-dialog {
                    transform: scale(1);
                    opacity: 1;
                }
                
                /* SweetAlert2 styling */
                .swal2-popup {
                    border-radius: 20px !important;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
                }
                .swal2-confirm, .swal2-cancel {
                    border-radius: 8px !important;
                }
            `}</style>



            <Row>
                <Col md="6">
                    {/* Card Ảnh đại diện */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>{tool.name}</Label>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col xs="12">
                                    <Label><b>Ảnh đại diện</b></Label>
                                    <div className="d-flex flex-column align-items-center mb-3" style={{ border: '1px solid #e3e3e3', padding: '15px', borderRadius: '8px' }}>
                                        {thumbnailPreview ? (
                                            <div style={{ position: 'relative', width: '200px', height: '200px', marginBottom: '15px' }}>
                                                <img
                                                    src={thumbnailPreview}
                                                    alt="Thumbnail"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/200x200?text=No+Image';
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ width: '200px', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', marginBottom: '15px', borderRadius: '4px' }}>
                                                <span className="text-muted">Chưa có ảnh</span>
                                            </div>
                                        )}

                                        <div className="d-flex flex-column w-100">
                                            <input
                                                type="file"
                                                id="thumbnail-upload"
                                                accept="image/*"
                                                className="d-none"
                                                onChange={handleThumbnailChange}
                                            />
                                            <div className="d-flex justify-content-center">
                                                <Button
                                                    color="secondary"
                                                    outline
                                                    className="mr-2"
                                                    onClick={() => document.getElementById('thumbnail-upload').click()}
                                                >
                                                    <i className="nc-icon nc-image mr-1"></i> Chọn ảnh
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    disabled={!thumbnailFile || uploadLoading}
                                                    onClick={uploadThumbnail}
                                                >
                                                    {uploadLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                                            Đang tải lên...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="nc-icon nc-cloud-upload-94 mr-1"></i> Cập nhật
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <small className="text-muted text-center mt-2">
                                                Định dạng hỗ trợ: JPG, PNG. Kích thước tối đa: 2MB
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* Card Mô tả công cụ */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Mô tả công cụ</Label>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col xs="12">
                                    <div className="d-flex flex-column mb-3">
                                        <FormGroup>
                                            <Input
                                                type="textarea"
                                                id="description"
                                                placeholder="Nhập mô tả chi tiết về công cụ..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value.slice(0, maxDescriptionLength))}
                                                rows={5}
                                                style={{
                                                    resize: 'vertical',
                                                    minHeight: '120px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <div className="d-flex justify-content-between mt-1">
                                                <small className="text-muted">
                                                    Mô tả ngắn gọn về chức năng của công cụ
                                                </small>
                                                <small className={`${description.length >= maxDescriptionLength ? 'text-danger' : 'text-muted'}`}>
                                                    {description.length}/{maxDescriptionLength} ký tự
                                                </small>
                                            </div>
                                        </FormGroup>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter className="d-flex justify-content-end bg-transparent border-0">
                            <Button
                                color="success"
                                disabled={descriptionLoading}
                                onClick={saveDescription}
                            >
                                {descriptionLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <><i className="nc-icon nc-check-2 mr-1"></i>Lưu mô tả</>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>

                    {/* Card Thông tin tải xuống */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Thông tin tải xuống</Label>
                                <input
                                    type="file"
                                    ref={toolFileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleToolFileUpload}
                                />
                                <Button
                                    color="info"
                                    size="sm"
                                    disabled={toolUploading}
                                    onClick={() => toolFileInputRef.current?.click()}
                                >
                                    {toolUploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-1"></span>
                                            {toolUploadProgress}%
                                        </>
                                    ) : (
                                        <><i className="nc-icon nc-cloud-upload-94 mr-1"></i> Tải lên</>
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Table bordered size="sm" className="mb-0">
                                <tbody>
                                    <tr>
                                        <td width="150"><b>Mã công cụ</b></td>
                                        <td>{tool.matool}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Tệp tin</b></td>
                                        <td>{tool.file}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Mật khẩu giải nén</b></td>
                                        <td>
                                            {editingPassword ? (
                                                <div className="d-flex align-items-center">
                                                    <Input
                                                        type="text"
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        style={{ width: '150px', marginRight: '8px' }}
                                                        size="sm"
                                                        placeholder="Nhập mật khẩu mới"
                                                    />
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        className="mr-1"
                                                        disabled={passwordLoading}
                                                        onClick={async () => {
                                                            if (!newPassword.trim()) {
                                                                Swal.fire({ icon: 'warning', title: 'Thông báo', text: 'Vui lòng nhập mật khẩu mới' });
                                                                return;
                                                            }
                                                            setPasswordLoading(true);
                                                            try {
                                                                await toolsService.updatePassword(tool.matool, newPassword);
                                                                setTool({ ...tool, passunrar: newPassword });
                                                                setEditingPassword(false);
                                                                Swal.fire({ icon: 'success', title: 'Thành công', text: 'Đã cập nhật mật khẩu' });
                                                            } catch (error) {
                                                                Swal.fire({ icon: 'error', title: 'Lỗi', text: error.message || 'Không thể cập nhật mật khẩu' });
                                                            } finally {
                                                                setPasswordLoading(false);
                                                            }
                                                        }}
                                                    >
                                                        {passwordLoading ? <span className="spinner-border spinner-border-sm"></span> : <i className="nc-icon nc-check-2"></i>}
                                                    </Button>
                                                    <Button
                                                        color="secondary"
                                                        size="sm"
                                                        onClick={() => { setEditingPassword(false); setNewPassword(''); }}
                                                    >
                                                        <i className="nc-icon nc-simple-remove"></i>
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center">
                                                    <span className="mr-2">{tool.passunrar || '-'}</span>
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="p-0"
                                                        onClick={() => { setEditingPassword(true); setNewPassword(tool.passunrar || ''); }}
                                                    >
                                                        <i className="nc-icon nc-ruler-pencil text-warning"></i>
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><b>Dung lượng</b></td>
                                        <td>{tool.fileSize ? formatFileSize(tool.fileSize) : '-'}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Người tải lên</b></td>
                                        <td>{tool.uploadedBy}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Xác thực tải về</b></td>
                                        <td>
                                            <CustomInput
                                                type="switch"
                                                id="auth-switch"
                                                checked={!!tool.downloadRequireAuth}
                                                label={tool.downloadRequireAuth ? "Có" : "Không"}
                                                onChange={handleAuthToggle}
                                                className="custom-switch-lg"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
                <Col md="6">
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Thuộc tính</Label>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => toggleAttributeModal()}
                                >
                                    <i className="nc-icon nc-simple-add mr-1"></i> Thêm
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            {attributes.length === 0 ? (
                                <div className="text-center text-muted">
                                    Chưa có thuộc tính nào được tạo
                                </div>
                            ) : (
                                <Table className="table-hover" style={{ marginTop: '10px', lineHeight: '15px' }}>
                                    <thead className="text-primary">
                                        <tr>
                                            <th>Tên thuộc tính</th>
                                            <th>Giá trị</th>
                                            <th>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attributes.map((attribute, index) => (
                                            <tr key={index}>
                                                <td><b>{attribute.name}</b></td>
                                                <td>{attribute.values.join(', ')}</td>
                                                <td>
                                                    <Button
                                                        color="warning"
                                                        size="sm"
                                                        onClick={() => toggleAttributeModal(index)}
                                                    >
                                                        <i className="nc-icon nc-ruler-pencil"></i> Sửa
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        className="ml-2"
                                                        onClick={() => deleteAttribute(index)}
                                                    >
                                                        <i className="nc-icon nc-simple-remove"></i> Xóa
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>

                    {/* Card Biến thể */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>Biến thể ({variations.length})</Label>
                        </CardHeader>
                        <CardBody>

                            {variations.length === 0 ? (
                                <div className="text-center text-muted">
                                    Không có biến thể nào. Vui lòng thêm thuộc tính trước.
                                </div>
                            ) : (
                                <>
                                    <Table className="table-hover mb-3">
                                        <thead className="text-primary">
                                            <tr>
                                                <th style={{ verticalAlign: 'middle' }}>Kích hoạt</th>
                                                {attributes.map((attr, index) => (
                                                    <th key={index} style={{ verticalAlign: 'middle' }}>{attr.name}</th>
                                                ))}
                                                <th style={{ verticalAlign: 'middle' }}>Ngày</th>
                                                <th style={{ verticalAlign: 'middle' }}>Giá</th>
                                                <th style={{ verticalAlign: 'middle' }}>Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {variations.map((variation, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <CustomInput
                                                            type="switch"
                                                            id={`switch-${index}`}
                                                            checked={variation.enabled}
                                                            onChange={() => toggleVariationStatus(index)}
                                                            className="custom-switch-lg"
                                                        />
                                                    </td>
                                                    {attributes.map((attr, attrIndex) => (
                                                        <td key={attrIndex}>
                                                            {variation.attributes[attr.name]}
                                                        </td>
                                                    ))}
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            value={variation.days || 0}
                                                            onChange={(e) => {
                                                                const updatedVariations = [...variations];
                                                                updatedVariations[index].days = parseInt(e.target.value) || 0;
                                                                setVariations(updatedVariations);
                                                            }}
                                                            className="text-right"
                                                            style={{ width: "90px" }}
                                                            min="0"
                                                        />
                                                    </td>
                                                    <td>
                                                        <InputGroup size="sm" className="price-input-group">
                                                            <Input
                                                                type="text"
                                                                value={formatCurrency(variation.price)}
                                                                onChange={(e) => {
                                                                    // Chỉ cho phép số và dấu chấm
                                                                    const value = e.target.value.replace(/[^\d.]/g, '');
                                                                    const updatedVariations = [...variations];
                                                                    updatedVariations[index].price = parseCurrency(value);
                                                                    setVariations(updatedVariations);
                                                                }}
                                                                className="text-right"
                                                                style={{
                                                                    minWidth: "100px",
                                                                    borderTopRightRadius: 0,
                                                                    borderBottomRightRadius: 0,
                                                                    borderRight: 'none'
                                                                }}
                                                            />
                                                            <InputGroupAddon addonType="append">
                                                                <InputGroupText style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '0 12px',
                                                                    height: '100%',
                                                                    borderTopRightRadius: '8px',
                                                                    borderBottomRightRadius: '8px',
                                                                    borderLeft: '1px solid #ced4da',
                                                                    backgroundColor: '#e9ecef'
                                                                }}>
                                                                    <span style={{
                                                                        fontFamily: 'Arial',
                                                                        fontSize: '14px',
                                                                        lineHeight: 1,
                                                                        fontWeight: '500'
                                                                    }}>VNĐ</span>
                                                                </InputGroupText>
                                                            </InputGroupAddon>
                                                        </InputGroup>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => deleteVariation(index)}
                                                        >
                                                            <i className="nc-icon nc-simple-remove"></i> Xóa
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* Footer với nút lưu */}
                                    <div className="d-flex justify-content-end mt-3 w-100">
                                        <Button
                                            color="success"
                                            onClick={saveAll}
                                        >
                                            <><i className="nc-icon nc-check-2 mr-1"></i>Lưu biến thể</>
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>

                    {/* KiotViet Integration Card - Phía dưới Biến thể */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '15px', marginBottom: '20px' }}>
                        <CardHeader>
                            <Label style={{ fontSize: '24px', color: 'DimGray', fontWeight: 'bold' }}>KiotViet Integration</Label>
                        </CardHeader>
                        <CardBody>
                            {/* Switch Liên kết KiotViet */}
                            <div className="d-flex align-items-center mb-3">
                                <CustomInput
                                    type="switch"
                                    id="kiotviet-enabled"
                                    label="Liên kết KiotViet"
                                    checked={kiotvietEnabled}
                                    onChange={() => setKiotvietEnabled(!kiotvietEnabled)}
                                    className="custom-switch-lg"
                                />
                            </div>

                            {kiotvietLoading ? (
                                <div className="text-center text-muted py-3">
                                    <div className="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
                                    Đang tải sản phẩm...
                                </div>
                            ) : (
                                <>
                                    {/* Variation Mappings Table */}
                                    {kiotvietEnabled && (
                                        variations.length > 0 ? (
                                            <>
                                                <Label><b>Mapping biến thể</b></Label>
                                                {(() => {
                                                    // Check if any variation has version/loại attribute
                                                    const hasVersionAttr = attributes.some(attr =>
                                                        attr.name.toLowerCase().includes('loại') ||
                                                        attr.name.toLowerCase().includes('version')
                                                    );

                                                    return (
                                                        <Table className="table-hover" style={{ marginTop: '10px', lineHeight: '15px' }}>
                                                            <thead className="text-primary">
                                                                <tr>
                                                                    {hasVersionAttr && <th>Loại</th>}
                                                                    <th>Thời hạn</th>
                                                                    <th>Sản phẩm KiotViet</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {variations.map((variation, index) => {
                                                                    const durationKey = Object.keys(variation.attributes || {}).find(k =>
                                                                        k.toLowerCase().includes('thời hạn') || k.toLowerCase().includes('hạn')
                                                                    );

                                                                    const expired = durationKey ? variation.attributes[durationKey] : '';
                                                                    const versionKey = Object.keys(variation.attributes || {}).find(k =>
                                                                        k.toLowerCase().includes('loại') || k.toLowerCase().includes('version')
                                                                    );
                                                                    const version = versionKey ? variation.attributes[versionKey] : '';
                                                                    const currentCode = getVariationProductCode(expired, version);

                                                                    return (
                                                                        <tr key={index}>
                                                                            {hasVersionAttr && <td><b>{version || '-'}</b></td>}
                                                                            <td><b>{expired || '-'}</b></td>
                                                                            <td>
                                                                                <Dropdown
                                                                                    isOpen={dropdownOpen[index]}
                                                                                    toggle={() => setDropdownOpen({ ...dropdownOpen, [index]: !dropdownOpen[index] })}
                                                                                    className="kiotviet-dropdown"
                                                                                >
                                                                                    <DropdownToggle caret style={{
                                                                                        width: '100%',
                                                                                        textAlign: 'left',
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                        backgroundColor: '#fff',
                                                                                        border: '1px solid #ced4da',
                                                                                        color: '#495057'
                                                                                    }}>
                                                                                        {currentCode ?
                                                                                            currentCode.toLowerCase()
                                                                                            : '-- Chọn sản phẩm --'
                                                                                        }
                                                                                    </DropdownToggle>
                                                                                    <DropdownMenu style={{ width: '100%', maxHeight: '200px', overflowY: 'auto' }}>
                                                                                        <DropdownItem
                                                                                            onClick={() => updateVariationMapping(expired, version, '', null)}
                                                                                        >
                                                                                            -- Chọn sản phẩm --
                                                                                        </DropdownItem>
                                                                                        <DropdownItem divider />
                                                                                        {filteredKiotvietProducts.map((product) => (
                                                                                            <DropdownItem
                                                                                                key={product.code}
                                                                                                active={currentCode?.toLowerCase() === product.code?.toLowerCase()}
                                                                                                onClick={() => {
                                                                                                    updateVariationMapping(
                                                                                                        expired,
                                                                                                        version,
                                                                                                        product.code,
                                                                                                        product.value || null
                                                                                                    );
                                                                                                }}
                                                                                            >
                                                                                                {product.code}
                                                                                            </DropdownItem>
                                                                                        ))}
                                                                                    </DropdownMenu>
                                                                                </Dropdown>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                    );
                                                })()}
                                            </>
                                        ) : (
                                            <div className="text-center text-muted py-3">
                                                <i className="nc-icon nc-alert-circle-i mr-2" style={{ fontSize: '24px' }}></i>
                                                <p className="mb-0">Vui lòng tạo biến thể trước khi thực hiện mapping KiotViet.</p>
                                            </div>
                                        )
                                    )}

                                    {/* Save Button - dùng success như Lưu biến thể */}
                                    <div className="d-flex justify-content-end mt-3">
                                        <Button
                                            color="success"
                                            onClick={saveKiotVietMapping}
                                            disabled={kiotvietSaveLoading}
                                        >
                                            {kiotvietSaveLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                                                    Đang lưu...
                                                </>
                                            ) : (
                                                <><i className="nc-icon nc-check-2 mr-1"></i>Lưu thay đổi</>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>



            {/* Modal thêm/sửa thuộc tính */}
            <Modal isOpen={attributeModal} toggle={() => toggleAttributeModal()} centered>
                <ModalHeader toggle={() => toggleAttributeModal()}>
                    {editAttributeIndex >= 0 ? 'Sửa thuộc tính' : 'Thêm thuộc tính mới'}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="attributeName">Tên thuộc tính</Label>
                            <Input
                                type="text"
                                id="attributeName"
                                placeholder="VD: Màu sắc, Kích thước..."
                                value={attributeName}
                                onChange={(e) => setAttributeName(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="attributeValues">Giá trị (phân cách bằng dấu phẩy)</Label>
                            <Input
                                type="textarea"
                                id="attributeValues"
                                placeholder="VD: Đỏ, Xanh, Vàng..."
                                value={attributeValues}
                                onChange={(e) => setAttributeValues(e.target.value)}
                            />
                            <small className="form-text text-muted">
                                Nhập các giá trị phân cách bằng dấu phẩy
                            </small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => toggleAttributeModal()}><i className="nc-icon nc-simple-remove mr-1"></i> Hủy</Button>
                    <Button color="primary" onClick={saveAttribute}>
                        {editAttributeIndex >= 0 ? (
                            <><i className="nc-icon nc-check-2 mr-1"></i> Cập nhật</>
                        ) : (
                            <><i className="nc-icon nc-simple-add mr-1"></i> Thêm</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div >
    );
}

export default ToolDetails; 